import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, Send } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import NewsletterForm from "@/components/NewsletterForm";

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [state, setState] = useState("idle");
  const [done, setDone] = useState(false);
  const mountedAt = useRef(Date.now());

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      await api.post("/contact", { ...form, elapsed: Date.now() - mountedAt.current });
      setDone(true);
      toast.success("Message sent — thank you for writing.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setState("idle");
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-cyanx/30 bg-fog/70 p-8"
        data-testid={T.contactSuccess}
      >
        <Send size={20} className="text-cyanx" />
        <h3 className="mt-4 font-serif text-2xl font-light text-ink">Your message is on its way.</h3>
        <p className="mt-3 text-sm leading-relaxed text-dim">
          A confirmation has landed in your inbox, and Isabella reads every note personally.
          Expect a reply when she's back at the desk.
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0B101C] px-4 py-3.5 text-sm text-ink placeholder:text-mute2 focus:border-cyanx/60 focus:outline-none focus:ring-1 focus:ring-cyanx/40";

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="c-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
            Name
          </label>
          <input
            id="c-name"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            data-testid={T.contactName}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="c-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
            Email
          </label>
          <input
            id="c-email"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="your@email.com"
            data-testid={T.contactEmail}
            className={inputCls}
          />
        </div>
      </div>
      <div>
        <label htmlFor="c-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mute2">
          Message
        </label>
        <textarea
          id="c-message"
          required
          rows={7}
          value={form.message}
          onChange={set("message")}
          placeholder="Media & speaking enquiries, reader letters, research questions — it all reaches Isabella."
          data-testid={T.contactMessage}
          className={`${inputCls} resize-none`}
        />
      </div>
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={set("website")}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
      />
      <button
        type="submit"
        disabled={state === "sending"}
        data-testid={T.contactSubmit}
        className="group flex items-center gap-3 rounded-full bg-cyanx px-8 py-4 font-mono text-[11px] uppercase tracking-[0.24em] text-void transition-all duration-300 hover:brightness-110 disabled:opacity-60"
      >
        {state === "sending" ? (
          <Loader2 size={15} className="animate-spin" />
        ) : (
          <>
            Send message
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute2">
        Rate-limited &amp; spam-protected — you'll get a confirmation email
      </p>
    </form>
  );
}

export default function Contact() {
  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-32 sm:px-8 lg:px-14 lg:pt-40">
      <Seo
        title="Contact Isabella MacLeod"
        description="Reader letters, media requests, and speaking invitations reach Isabella directly. Join the reader list for launch news and essays."
        path="/contact"
      />
      <div className="grid gap-16 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-7">
          <FadeUp className="mb-12">
            <Eyebrow className="mb-6">Get in Touch</Eyebrow>
            <h1 className="font-serif text-5xl font-light leading-[1.02] tracking-tight text-ink sm:text-6xl lg:text-7xl">
              Write to <span className="serif-italic text-cyanx">Isabella.</span>
            </h1>
            <p className="mt-7 max-w-lg text-base font-light leading-relaxed text-dim sm:text-lg">
              Reader letters, media requests, speaking invitations, and research questions
              are all welcome. Every message lands directly with Isabella.
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <ContactForm />
          </FadeUp>
        </div>

        <aside className="lg:col-span-5">
          <FadeUp delay={0.15} className="rounded-2xl border border-white/[0.07] bg-fog/50 p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyanx">
              Prefer book news instead?
            </p>
            <h2 className="mt-4 font-serif text-3xl font-light text-ink">
              Join the <span className="serif-italic text-cyanx">reader list</span>
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-dim">
              Launches, essays, and behind-the-book notes from Nova Scotia. Double opt-in,
              unsubscribe in one click.
            </p>
            <div className="mt-7">
              <NewsletterForm compact />
            </div>
          </FadeUp>

          <FadeUp delay={0.2} className="mt-8 rounded-2xl border border-white/[0.07] p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute2">Speaking topics</p>
            <ul className="mt-5 space-y-3 text-sm leading-relaxed text-dim">
              <li className="flex gap-3"><span className="text-cyanx">—</span>Nanotechnology &amp; transhumanism</li>
              <li className="flex gap-3"><span className="text-cyanx">—</span>Morgellons &amp; chronic Lyme awareness</li>
              <li className="flex gap-3"><span className="text-cyanx">—</span>Targeted individuals &amp; bio-experimentation</li>
              <li className="flex gap-3"><span className="text-cyanx">—</span>Writing through trauma</li>
            </ul>
          </FadeUp>
        </aside>
      </div>
    </div>
  );
}
